// src/services/api.js
import axios from "axios";

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single in-flight refresh shared by all concurrent 401s. Without this, several
// requests expiring at once would each call /token/refresh/; with refresh-token
// rotation the first call invalidates the others' (now blacklisted) token and
// they would all fail, logging the user out mid-session.
let refreshPromise = null;

// Response interceptor: on a 401, try once to refresh the access token and
// replay the original request; only fall back to logout+redirect if the refresh
// itself fails (or the failing call WAS the refresh).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const onLogin = window.location.pathname.includes("login");
    const isRefreshCall = original?.url?.includes("/token/refresh");

    if (status === 401 && original && !original._retry && !isRefreshCall && !onLogin) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          // Lazy import breaks the api <-> authService circular import at load.
          refreshPromise = import("./authService")
            .then((m) => m.refreshToken())
            .finally(() => {
              refreshPromise = null;
            });
        }
        const newToken = await refreshPromise;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        // Refresh failed - fall through to the logout/redirect below.
      }
    }

    if (status === 401 && !onLogin) {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default api;
